<template>
  <div
    v-if="!!logData"
    data-cy="deal-communication-log-wrapper"
    class="flex max-w-80 min-w-50 cursor-pointer flex-col space-x-2"
    @click="openActivityModal({ model })"
  >
    <last-call
      v-if="isCallLogData(logData)"
      data-cy="deal-communication-log-call"
      :model="{ last_call_session: logData, id: model.id }"
      is-all-activity
    />
    <last-email-sent
      v-else-if="isEmailLogData(logData)"
      data-cy="deal-communication-log-email"
      :model="{
        latest_email_sent: emailData,
        id: model.id,
      }"
      is-all-activity
    />
    <recent-note
      v-else-if="isNoteLogData(logData)"
      data-cy="deal-communication-log-note"
      :model="{ latest_note: logData, id: model.id }"
      is-all-activity
    />
    <last-sms
      v-else-if="isSmsLogData(logData)"
      data-cy="deal-communication-log-sms"
      :model="logData"
    />
    <last-task
      v-else-if="isTaskLogData(logData)"
      data-cy="deal-communication-log-task"
      :model="logData"
    />
  </div>
  <div v-else data-cy="deal-communication-log-empty">-</div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useActivityModal } from "@/composables/useActivityModal";
import { CommunicationType } from "@/enums/communicationLogs";

import LastCall from "@/components/deals/LastCall.vue";
import LastEmailSent from "@/components/deals/LastEmailSent.vue";
import LastSms from "@/components/deals/LastSms.vue";
import LastTask from "@/components/deals/LastTask.vue";
import RecentNote from "@/components/deals/RecentNote.vue";

import type { IApplication, LatestSentEmail } from "@/models/applications";
import type {
  CallSession,
  DealCommunicationLog,
  EmailThread,
  SmsLogData,
  TaskLogData,
} from "@/models/communicationLogs";
import type { Note } from "@/models/notes";

const { value, model } = defineProps<{
  value: DealCommunicationLog | null;
  model: IApplication;
}>();

const { openActivityModal } = useActivityModal();

const logData = computed(() => value?.data || null);

const emailData = computed<LatestSentEmail | null>(() => {
  if (!isEmailLogData(logData.value)) {
    return null;
  }

  return {
    id: logData.value.id,
    sender_name: logData.value.sender?.name || "",
    template_name: logData.value?.template_name || "",
    subject: logData.value.subject || "",
    timestamp: logData.value.created_at,
  };
});

const isEmailLogData = (
  data: DealCommunicationLog["data"] | null
): data is EmailThread => value?.type === CommunicationType.email;

const isCallLogData = (
  data: DealCommunicationLog["data"] | null
): data is CallSession => value?.type === CommunicationType.call;

const isTaskLogData = (
  data: DealCommunicationLog["data"] | null
): data is TaskLogData => value?.type === CommunicationType.task;

const isSmsLogData = (
  data: DealCommunicationLog["data"] | null
): data is SmsLogData => value?.type === CommunicationType.sms;

const isNoteLogData = (
  data: DealCommunicationLog["data"] | null
): data is Note => value?.type === CommunicationType.note;
</script>
